/**
 * Shared score primitives used by FallenEdgePanel and ShortIntelligencePanel.
 *
 * Renders 0–100 scores with beginner-friendly visuals. Missing values render
 * "(unavailable)" rather than 0, so a missing input never looks like a real low
 * score. No raw JSON, no raw ISO timestamps. Squeeze warnings get their own
 * loud component because the operator brief requires prominent labeling.
 */
import React from "react";

export type ScoreTone =
  | "neutral"
  | "bullish"   // reversal lean
  | "bearish"   // short / breakdown lean
  | "warning"   // avoid / squeeze risk
  | "muted";

const TONE_BAR: Record<ScoreTone, string> = {
  neutral: "bg-slate-500",
  bullish: "bg-emerald-500",
  bearish: "bg-rose-500",
  warning: "bg-amber-500",
  muted: "bg-slate-700",
};

const TONE_TEXT: Record<ScoreTone, string> = {
  neutral: "text-slate-300",
  bullish: "text-emerald-300",
  bearish: "text-rose-300",
  warning: "text-amber-300",
  muted: "text-slate-500",
};

function clampPct(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function ScoreBar({
  label,
  value,
  tone = "neutral",
  hint,
}: {
  label: string;
  value: number | null | undefined;
  tone?: ScoreTone;
  hint?: string;
}) {
  const pct = clampPct(value);
  const display = pct === null ? "(unavailable)" : `${pct} / 100`;
  return (
    <div className="space-y-1" data-testid={`score-bar-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">
          {label}
        </span>
        <span className={`text-xs font-bold ${pct === null ? TONE_TEXT.muted : TONE_TEXT[tone]}`}>
          {display}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
        {pct === null ? (
          <div className="h-full w-full bg-slate-800" />
        ) : (
          <div
            className={`h-full ${TONE_BAR[tone]} transition-all`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      {hint ? <p className="text-[10px] text-slate-500 leading-snug">{hint}</p> : null}
    </div>
  );
}

export function ScoreChip({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number | null | undefined;
  tone?: ScoreTone;
}) {
  const pct = clampPct(value);
  const display = pct === null ? "—" : String(pct);
  const toneClass: Record<ScoreTone, string> = {
    neutral: "border-slate-600 bg-slate-800/60 text-slate-200",
    bullish: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    bearish: "border-rose-500/40 bg-rose-500/10 text-rose-200",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    muted: "border-slate-700 bg-slate-900/60 text-slate-500",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11px] font-bold ${toneClass[pct === null ? "muted" : tone]}`}
      title={`${label}: ${display}/100`}
    >
      <span className="uppercase tracking-widest text-[9px] opacity-80">{label}</span>
      <span>{display}</span>
    </span>
  );
}

export function AdvisoryOnlyBadge() {
  return (
    <span
      data-testid="advisory-only-badge"
      className="inline-flex items-center gap-1.5 rounded border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-sky-200"
    >
      Advisory only · no orders
    </span>
  );
}

export function SqueezeWarningBadge({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <div
      data-testid="squeeze-warning-badge"
      role="alert"
      className="rounded-lg border border-amber-400/60 bg-amber-500/15 px-3 py-2 text-amber-100 flex items-start gap-2"
    >
      <span className="text-base leading-none mt-0.5" aria-hidden>⚠</span>
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-amber-300">
          Short Squeeze Risk
        </div>
        <div className="text-xs leading-snug">{message}</div>
      </div>
    </div>
  );
}

export function DataQualityChip({
  quality,
  missing,
}: {
  quality: string | null | undefined;
  missing: string[] | null | undefined;
}) {
  const q = (quality || "unknown").toLowerCase();
  const tone =
    q === "good" || q === "high"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
      : q === "limited" || q === "medium"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
      : "border-rose-500/40 bg-rose-500/10 text-rose-200";
  const missingLabel = Array.isArray(missing) && missing.length > 0
    ? ` · missing: ${missing.join(", ")}`
    : "";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone}`}
      title={`Data quality: ${q}${missingLabel}`}
    >
      Data: {q}
      {Array.isArray(missing) && missing.length > 0 ? (
        <span className="font-normal normal-case opacity-80"> · {missing.length} missing</span>
      ) : null}
    </span>
  );
}

/** Format ISO UTC → "Jun 4, 10:32 AM ET" (America/New_York). Returns "(unavailable)" on bad input. */
export function formatGeneratedAtET(iso: string | null | undefined): string {
  if (!iso) return "(unavailable)";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "(unavailable)";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${fmt.format(d)} ET`;
  } catch {
    return "(unavailable)";
  }
}

export function formatLossPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return "(unavailable)";
  const v = Math.round(pct * 100) / 100;
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}

export function humanize(token: string | null | undefined): string {
  if (!token) return "(unavailable)";
  return token
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
